import { useState, useCallback, useRef, useEffect } from "react";
import PageShell from "../../../components/PageShell";
import DataTable from "../../../components/DataTable";
import DataForm from "../../../components/DataForm";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../../components/ui/dialog";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import ConfirmDialog from "../../../components/ConfirmDialog";
import {
  ContextMenu,
  ContextMenuTrigger,
  ContextMenuContent,
  ContextMenuItem,
} from "../../../components/ui/context-menu";
import {
  useLocations,
  useSaveLocation,
  useArchiveLocation,
  useBookingServices,
} from "../hooks/useLocations";
import toast from "react-hot-toast";
import { useTranslation } from "../../../i18n";
import { useNavigate, useSearchParams } from "react-router-dom";

const COLUMNS = [
  { header: "col.id", accessor: "id", type: "number" },
  { header: "col.name", accessor: "name", type: "text" },
  { header: "col.address", accessor: "address", type: "text" },
  { header: "col.isLocationActive", accessor: "isActive", type: "boolean" },
  { header: "col.coverUrl", accessor: "coverUrl", type: "text" },
  {
    header: "col.easyweekUuid",
    accessor: (row) => (row.easyweekInfo ? row.easyweekInfo.locationUuid : ""),
    type: "text",
  },
  {
    header: "col.appointmentMaxPlayers",
    accessor: "appointmentMaxPlayers",
    type: "number",
  },
];

const FORM_FIELDS = [
  { name: "id", label: "col.id", type: "number", disabled: true },
  { name: "name", label: "form.name", type: "text" },
  { name: "address", label: "form.address", type: "text" },
  { name: "latitude", label: "form.latitude", type: "number", required: false },
  {
    name: "longitude",
    label: "form.longitude",
    type: "number",
    required: false,
  },
  { name: "coverUrl", label: "form.coverUrl", type: "image", required: false },
  { name: "isActive", label: "form.active", type: "boolean", required: false },
  {
    name: "easyweekLocationUuid",
    label: "form.easyweekLocationUuid",
    type: "text",
    required: false,
  },
  {
    name: "appointmentMaxPlayers",
    label: "form.appointmentMaxPlayers",
    type: "number",
    required: false,
  },
];

const CREATE_DEFAULTS = {
  name: "",
  address: "",
  isActive: true,
  latitude: null,
  longitude: null,
  coverUrl: "",
  easyweekLocationUuid: "",
  appointmentMaxPlayers: 8,
  bookingServices: [],
  zones: [],
};

export default function LocationsPage() {
  const { t } = useTranslation();
  const { data: locations, isLoading } = useLocations();
  const saveMutation = useSaveLocation();
  const archiveMutation = useArchiveLocation();
  const [editingRow, setEditingRow] = useState(null);
  const [isNew, setIsNew] = useState(false);
  const [values, setValues] = useState(null);
  const [archiveTarget, setArchiveTarget] = useState(null);
  const zonesContainerRef = useRef(null);
  const bookingServicesContainerRef = useRef(null);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { data: bookingServicesData, isLoading: isLoadingBookingServices } =
    useBookingServices(editingRow?.id);

  const openEdit = useCallback((row) => {
    setEditingRow(row);
    // Flatten easyweekInfo into flat fields for the form
    const flat = {
      ...row,
      easyweekLocationUuid: row.easyweekInfo?.locationUuid || "",
      bookingServices: [],
      zones: row.zones ? [...row.zones] : [],
    };
    delete flat.easyweekInfo;
    setValues(flat);
    setIsNew(false);
  }, []);

  const openCreate = useCallback(() => {
    setEditingRow(CREATE_DEFAULTS);
    setValues({ ...CREATE_DEFAULTS });
    setIsNew(true);
  }, []);

  // Support deep-linking straight to the zone-creation UI from the computer
  // form: when ?editZone=<locationId>&returnTo=... is present, auto-open that
  // location's edit dialog and scroll to its zones section.
  const autoOpenedLocationRef = useRef(null);
  useEffect(() => {
    const editZoneId = searchParams.get("editZone");
    if (!editZoneId || isLoading || !locations || locations.length === 0)
      return;
    const key = String(editZoneId);
    if (autoOpenedLocationRef.current === key) return;
    autoOpenedLocationRef.current = key;
    const match = locations.find((l) => String(l.id) === key);
    if (!match) return;
    openEdit(match);
    // Scroll to the zones section and focus its "Add" button after the dialog
    // render settles.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (!zonesContainerRef.current) return;
        zonesContainerRef.current.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
        });
        const addBtn = zonesContainerRef.current.querySelector("button");
        if (addBtn) addBtn.focus();
      });
    });
  }, [searchParams, isLoading, locations, openEdit]);

  const buildApiPayload = (formValues) => {
    const payload = { ...formValues };

    // Rebuild easyweek_info from flat fields
    const locUuid = (payload.easyweekLocationUuid || "").trim();

    delete payload.easyweekLocationUuid;

    if (locUuid) {
      payload.easyweekInfo = {
        locationUuid: locUuid,
      };
    }

    // Pass booking services (enabled flags) from the form state
    payload.bookingServices = payload.bookingServices || [];

    // Ensure zones is an array
    payload.zones = payload.zones || [];

    return payload;
  };

  const handleSave = () => {
    if (!values) return;
    const apiValues = buildApiPayload(values);

    saveMutation.mutate(apiValues, {
      onSuccess: () => {
        toast.success(
          isNew ? t("toast.recordCreated") : t("toast.recordUpdated"),
        );
        setEditingRow(null);
        setValues(null);
        // If the Locations page was opened with a returnTo param (e.g. from
        // the computer form's "need a new zone" link), navigate back.
        const returnTo = searchParams.get("returnTo");
        if (returnTo) {
          navigate(returnTo);
          return;
        }
      },
      onError: (err) => {
        const message = err?.message || t("toast.recordSaveFailed");
        toast.error(message);
      },
    });
  };

  const handleAddZone = () => {
    setValues((prev) => ({
      ...prev,
      zones: [...(prev.zones || []), { id: null, name: "" }],
    }));
    // Auto-scroll and focus the new zone input after state update
    requestAnimationFrame(() => {
      if (!zonesContainerRef.current) return;
      const lastRow = zonesContainerRef.current.lastElementChild;
      if (!lastRow) return;
      lastRow.scrollIntoView({ behavior: "smooth", block: "nearest" });
      const input = lastRow.querySelector("input:not([type=hidden])");
      if (input) {
        input.focus();
      }
    });
  };

  const handleRemoveZone = (index) => {
    setValues((prev) => ({
      ...prev,
      zones: (prev.zones || []).filter((_, i) => i !== index),
    }));
  };

  const handleZoneNameChange = (index, newName) => {
    setValues((prev) => {
      const updatedZones = [...(prev.zones || [])];
      updatedZones[index] = { ...updatedZones[index], name: newName };
      return { ...prev, zones: updatedZones };
    });
  };

  const renderArchiveMenu = useCallback(
    (row, children) => (
      <ContextMenu>
        <ContextMenuTrigger asChild>{children}</ContextMenuTrigger>
        <ContextMenuContent>
          <ContextMenuItem
            className="bg-c-red-500 hover:bg-c-red-600 text-white"
            onClick={() => setArchiveTarget(row)}
          >
            {t("common.archive")}
          </ContextMenuItem>
        </ContextMenuContent>
      </ContextMenu>
    ),
    [],
  );

  const handleArchive = () => {
    if (!archiveTarget) return;
    archiveMutation.mutate(archiveTarget.id, {
      onSuccess: () => {
        toast.success(t("toast.locationArchived"));
        setArchiveTarget(null);
      },
      onError: () => toast.error(t("toast.locationArchiveFailed")),
    });
  };

  return (
    <PageShell
      title={t("page.locations")}
      actions={<Button onClick={openCreate}>{t("common.add")}</Button>}
    >
      {isLoading ? (
        <p className="text-c-gray-400">{t("common.loading")}</p>
      ) : (
        <DataTable
          columns={COLUMNS}
          data={locations}
          onRowClick={openEdit}
          renderContextMenu={renderArchiveMenu}
        />
      )}
      <Dialog
        open={!!editingRow}
        onOpenChange={(o) => {
          if (!o) {
            setEditingRow(null);
            setValues(null);
          }
        }}
      >
        <DialogContent className="left-auto right-0 top-0 translate-x-0 translate-y-0 h-dvh max-h-none rounded-none sm:rounded-none border-r-0 border-t-0 border-b-0 data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right min-w-0 sm:min-w-[480px] grid-rows-[auto_1fr_auto]">
          <DialogHeader>
            <DialogTitle>
              {isNew ? t("dialog.addLocation") : t("dialog.editRecord")}
            </DialogTitle>
          </DialogHeader>
          <form
            id="edit-form"
            onSubmit={(e) => {
              e.preventDefault();
              handleSave();
            }}
            className="flex-1 overflow-y-auto px-2"
          >
            <DataForm
              fields={FORM_FIELDS}
              values={values}
              onChange={setValues}
            />

            {/* Booking Services Section */}
            {editingRow?.id && !isLoadingBookingServices && (
              <div
                ref={bookingServicesContainerRef}
                className="mt-6 pt-4 border-t border-c-gray-400/30"
              >
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-medium text-white">
                    {t("form.bookingServices")}
                  </h3>
                </div>

                {(!bookingServicesData || bookingServicesData.length === 0) && (
                  <p className="text-sm text-c-gray-400">
                    {t("common.noData")}
                  </p>
                )}

                {bookingServicesData?.map((svc) => (
                  <label
                    key={svc.ewServiceUuid}
                    className="flex items-center gap-2 mb-2 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      className="w-4 h-4 rounded border-c-gray-400 bg-c-dark-800"
                      checked={
                        values?.bookingServices?.some(
                          (bs) =>
                            bs.ew_service_uuid === svc.ewServiceUuid &&
                            bs.enabled,
                        ) || false
                      }
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setValues((prev) => {
                          const current = prev.bookingServices || [];
                          const existing = current.find(
                            (bs) => bs.ew_service_uuid === svc.ewServiceUuid,
                          );
                          let updated;
                          if (existing) {
                            updated = current.map((bs) =>
                              bs.ew_service_uuid === svc.ewServiceUuid
                                ? { ...bs, enabled: checked }
                                : bs,
                            );
                          } else {
                            updated = [
                              ...current,
                              {
                                ew_service_uuid: svc.ewServiceUuid,
                                enabled: checked,
                              },
                            ];
                          }
                          return { ...prev, bookingServices: updated };
                        });
                      }}
                    />
                    <span className="text-sm text-white">{svc.name}</span>
                    <span className="text-xs text-c-gray-400">
                      ({svc.duration_minutes} min)
                    </span>
                  </label>
                ))}
              </div>
            )}

            {/* Zones Section */}
            <div
              ref={zonesContainerRef}
              className="mt-6 pt-4 border-t border-c-gray-400/30"
            >
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-medium text-white">
                  {t("playtime.zones")}
                </h3>
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={handleAddZone}
                >
                  {t("common.add")}
                </Button>
              </div>

              {(!values?.zones || values.zones.length === 0) && (
                <p className="text-sm text-c-gray-400">{t("common.noData")}</p>
              )}

              {values?.zones?.map((zone, index) => (
                <div key={index} className="flex items-center gap-2 mb-2">
                  <input
                    type="hidden"
                    name={`zone-id-${index}`}
                    value={zone.id || ""}
                  />
                  <Input
                    type="text"
                    value={zone.name || ""}
                    onChange={(e) =>
                      handleZoneNameChange(index, e.target.value)
                    }
                    placeholder={t("form.zoneName")}
                    className="flex-1"
                  />
                  <Button
                    type="button"
                    variant="destructive"
                    size="sm"
                    onClick={() => handleRemoveZone(index)}
                  >
                    {t("common.delete")}
                  </Button>
                </div>
              ))}
            </div>
          </form>
          <DialogFooter className="flex gap-2">
            <Button type="submit" form="edit-form">
              {t("common.save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <ConfirmDialog
        open={!!archiveTarget}
        onOpenChange={(o) => {
          if (!o) setArchiveTarget(null);
        }}
        itemName={`${t("page.locations").toLowerCase()} "${archiveTarget?.name}"`}
        onConfirm={handleArchive}
      />
    </PageShell>
  );
}
